import axios from 'axios';
const API_URL = process.env.VUE_APP_API_URL;

class EventsService {
  async search(query, limit, offset, logTypes = [], sort = [], sources = [], searchAfter = null, timeRange = null) {
    try {
      const payload = {
        query: query,
        limit: limit,
        offset: searchAfter ? 0 : offset,
        log_types: logTypes,
        sort: sort
      };
      if (searchAfter) payload.search_after = searchAfter;
      if (sources.length > 0) payload.sources = sources;
      if (timeRange && timeRange.start != null) {
        payload.time_range = { start: timeRange.start, end: timeRange.end ?? null };
      }
      const { data } = await axios.post(API_URL + '/query/event/search', payload);
      return data;
    } catch(error) {
      if (error.response) {
        if (error.response.status == 429) {
          return { status: "ratelimited", data: {} }
        } else if (error.response.status == 401) {
          return { status: "unauthorized", data: {} }
        } else if (error.response.status == 403) {
          return { status: "forbidden", data: {} }
        } else if (error.response.status == 504) {
          return { status: "timeout", message: "Query timeout - too many results. Try narrowing your time range or adding more specific filters.", data: {} }
        } else if (error.response.status == 400) {
          return {
            status: "error",
            message: error.response.data.message || "Invalid query syntax",
            data: {}
          }
        } else if (error.response.data?.message) {
          // Any other status that carried an explanation — e.g. 502 when shards
          // failed and the results would have been incomplete. Surfacing the
          // server's message beats a generic failure the user cannot act on.
          return { status: "error", message: error.response.data.message, data: {} }
        } else {
          return { status: "failed", data: {} }
        }
      }
      // Network error or client timeout (no response from server)
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('Network Error')) {
        return { status: "timeout", message: "Query timeout - too many results. Try narrowing your time range or adding more specific filters.", data: {} }
      }
      return { status: "failed", data: {} }
    }
  }

  async aggregate(query, logTypes, field, aggregations = ['terms', 'cardinality'], termsSize = 10, backgroundFilter = null, compositeFields = null, sources = []) {
    try {
      const payload = {
        query: query,
        log_types: logTypes,
        field: field,
        aggregations: aggregations,
        terms_size: termsSize
      }
      if (sources.length > 0) payload.sources = sources;
      if (backgroundFilter) {
        payload.background_filter = backgroundFilter
      }
      if (compositeFields) {
        payload.composite_fields = compositeFields
      }
      const { data } = await axios.post(API_URL + '/query/event/aggregate', payload);
      return data;
    } catch(error) {
      if (error.response) {
        if (error.response.status == 429) {
          return { status: "ratelimited", data: {} }
        } else if (error.response.status == 401) {
          return { status: "unauthorized", data: {} }
        } else if (error.response.status == 403) {
          return { status: "forbidden", data: {} }
        } else if (error.response.status == 504) {
          return { status: "timeout", message: "Aggregation timeout - too many results to aggregate. Try narrowing your time range or adding more specific filters.", data: {} }
        } else if (error.response.status == 400) {
          return {
            status: "error",
            message: error.response.data.message || "Invalid request",
            data: {}
          }
        } else if (error.response.data?.message) {
          // See the note in search(): pass through any server-supplied reason,
          // such as the 502 raised when an aggregation lost shards.
          return { status: "error", message: error.response.data.message, data: {} }
        } else {
          return { status: "failed", data: {} }
        }
      }
      // Network error or client timeout (no response from server)
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('Network Error')) {
        return { status: "timeout", message: "Aggregation timeout - too many results to aggregate. Try narrowing your time range or adding more specific filters.", data: {} }
      }
      return { status: "failed", data: {} }
    }
  }

  async aggregateMultiCardinality(query, logTypes, fields, filterField = null, filterValue = null, sources = []) {
    try {
      const payload = {
        query: query,
        log_types: logTypes,
        fields: fields,
        aggregations: ['multi_cardinality']
      };
      if (sources.length > 0) payload.sources = sources;

      // Only add filter if provided
      if (filterField && filterValue !== null) {
        payload.filter_field = filterField;
        payload.filter_value = filterValue;
      }

      const { data } = await axios.post(API_URL + '/query/event/aggregate', payload);
      return data;
    } catch(error) {
      if (error.response) {
        if (error.response.status == 429) {
          return { status: "ratelimited", data: {} }
        } else if (error.response.status == 401) {
          return { status: "unauthorized", data: {} }
        } else if (error.response.status == 403) {
          return { status: "forbidden", data: {} }
        } else if (error.response.status == 504) {
          return { status: "timeout", message: "Cardinality timeout - too many results to count. Try narrowing your time range or adding more specific filters.", data: {} }
        } else if (error.response.status == 400) {
          return {
            status: "error",
            message: error.response.data.message || "Invalid request",
            data: {}
          }
        } else if (error.response.data?.message) {
          // See the note in search(): pass through any server-supplied reason,
          // such as the 502 raised when an aggregation lost shards.
          return { status: "error", message: error.response.data.message, data: {} }
        } else {
          return { status: "failed", data: {} }
        }
      }
      // Network error or client timeout (no response from server)
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('Network Error')) {
        return { status: "timeout", message: "Cardinality timeout - too many results to count. Try narrowing your time range or adding more specific filters.", data: {} }
      }
      return { status: "failed", data: {} }
    }
  }

  // Export documents via dedicated endpoint (returns gzipped JSONL, gzipped CSV, or ZIP of CSVs)
  async exportDocuments(query, logTypes = [], format = 'jsonl', sources = []) {
    try {
      const payload = { query, log_types: logTypes, format };
      if (sources.length > 0) payload.sources = sources;
      const response = await axios.post(
        API_URL + '/query/event/search/export',
        payload,
        { responseType: 'blob', timeout: 180000 }
      );

      // Get filename from Content-Disposition header or generate one
      const disposition = response.headers['content-disposition'];
      const unixTs = Math.floor(Date.now() / 1000);
      const logDesc = logTypes.length === 1 ? logTypes[0] : 'logs';
      const defaultExt = format === 'csv' ? (logTypes.length === 1 ? '.csv.gz' : '.zip') : '.jsonl.gz'
      let filename = `${unixTs}_${logDesc}${defaultExt}`;
      if (disposition) {
        const match = disposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].trim();
      }

      // Trigger download
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);

      return { status: 'success' };
    } catch (error) {
      if (error.response) {
        if (error.response.status === 429) return { status: 'ratelimited' };
        if (error.response.status === 401) return { status: 'unauthorized' };
        if (error.response.status === 403) return { status: 'forbidden' };
        if (error.response.status === 504) return { status: 'timeout' };
      }
      return { status: 'failed' };
    }
  }

  // Export aggregated values via dedicated endpoint (returns gzipped CSV or JSONL)
  async exportAggregateValues(query, logTypes, field, filterField = null, filterValue = null, format = 'csv', sources = []) {
    try {
      const payload = { query, log_types: logTypes, field, format };
      if (sources.length > 0) payload.sources = sources;
      if (filterField && filterValue !== null) {
        payload.filter_field = filterField;
        payload.filter_value = filterValue;
      }

      const response = await axios.post(
        API_URL + '/query/event/aggregate/export',
        payload,
        { responseType: 'blob', timeout: 180000 }
      );

      // Get filename from header or generate
      const disposition = response.headers['content-disposition'];
      const unixTs = Math.floor(Date.now() / 1000);
      const logDesc = logTypes.length === 1 ? logTypes[0] : 'logs';
      let filename = `${unixTs}_${logDesc}_${field}_values.${format === 'jsonl' ? 'jsonl' : 'csv'}.gz`;
      if (disposition) {
        const match = disposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].trim();
      }

      // Trigger download
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);

      return { status: 'success' };
    } catch (error) {
      if (error.response) {
        if (error.response.status === 429) return { status: 'ratelimited' };
        if (error.response.status === 401) return { status: 'unauthorized' };
        if (error.response.status === 403) return { status: 'forbidden' };
        if (error.response.status === 504) return { status: 'timeout' };
      }
      return { status: 'failed' };
    }
  }
}

export default new EventsService();
